/**
 * The JSON boundary, in TypeScript.
 *
 * These types mirror the C++ structs in `engine/include/planforge/`. They are hand
 * written, and nothing generates or checks them at build time — the guard is the
 * engine's own shape test (`engine/tests/test_plan.cpp`), which asserts that every
 * key declared here is actually emitted. Add a field to the engine and update this
 * file in the same change; add one here that the engine does not emit and the
 * compiler will not save you, but the first consumer will.
 */

/**
 * A length, in integer sixteenths of an inch.
 *
 * One unit spans the whole seam — engine internals, JSON, and this file — so there
 * is no conversion layer to get wrong. Real lumber is fractional and floats drift
 * under accumulation, which is why nothing here is ever a fraction of a unit. The
 * brand keeps a bare number from being passed where a length belongs.
 */
export type Sixteenths = number & { readonly __brand: unique symbol };

export const SIXTEENTHS_PER_INCH = 16;
export const SIXTEENTHS_PER_FOOT = SIXTEENTHS_PER_INCH * 12;

/** `inches(90)` — the UI's units, converted once, here. */
export function inches(n: number): Sixteenths {
  return (n * SIXTEENTHS_PER_INCH) as Sixteenths;
}

export function feet(n: number): Sixteenths {
  return (n * SIXTEENTHS_PER_FOOT) as Sixteenths;
}

/** Whole inches plus `numerator/denominator`, e.g. `fraction(7, 1, 2)` for 7 1/2". */
export function fraction(wholeInches: number, numerator: number, denominator: number): Sixteenths {
  return (wholeInches * SIXTEENTHS_PER_INCH +
    Math.trunc((numerator * SIXTEENTHS_PER_INCH) / denominator)) as Sixteenths;
}

// ---- spec ------------------------------------------------------------------

/**
 * The schema version this wrapper writes.
 *
 * Not the only one the engine builds: it accepts everything from
 * `OLDEST_SPEC_VERSION` up to this, and builds each as the shed that version was
 * sold as, so a spec stored before a geometry change still regenerates the plan
 * somebody paid for. Every plan carries both numbers in its envelope.
 */
export const SPEC_VERSION = 18;

/** The oldest spec version the engine will still build. */
export const OLDEST_SPEC_VERSION = 1;

export type Kind = "shed";
/**
 * The roof's shape. `"gable"` is two slopes meeting at a ridge over the middle of the
 * width. `"leanTo"` is one slope across the whole width, rising from the left wall to
 * the right: the right wall is framed taller by the roof's climb, the front and back
 * are its rake ends, and every rafter runs tail to tail, notched over both walls.
 * Spec version 7 and later.
 *
 * `"saltbox"` is a gable with its ridge off the middle — two thirds of the way across
 * unless `RoofSpec.ridgeTwelfths` moves it: a long slope from the left wall, a short one
 * down to a right wall framed taller by the difference, both at the one pitch. Its eaves
 * are at two heights, so nothing can tie them, and the engine normalizes its support to
 * `"ridgeBeam"`. Spec version 9 and later.
 */
export type RoofStyle = "gable" | "leanTo" | "saltbox";

/**
 * How the roof carries itself, which is two different buildings.
 *
 * A pitched roof pushes its walls apart, and something has to take that push.
 * `"tied"` closes the triangle with a tie across every rafter pair: the ridge then
 * carries almost nothing and is a board. `"ridgeBeam"` leaves the space open to the
 * peak, so the ridge has to carry half the roof to ground — a sized, unspliced beam
 * on posts down through the gable walls, which costs lumber and caps how big the shed
 * can be.
 *
 * `"bearing"` is a lean-to's, and only a lean-to's: a single slope seated level on a
 * wall at each end pushes on neither, so nothing ties it and no ridge needs a post. The
 * engine normalizes a lean-to to it and refuses it on a gable. A saltbox is normalized
 * to `"ridgeBeam"` the same way, because a beam is the one answer it has.
 */
export type RoofSupport = "tied" | "ridgeBeam" | "bearing";
export type Foundation = "skids" | "piers" | "slab";
export type Siding = "t111" | "lap" | "panel";
export type WallName = "front" | "back" | "left" | "right";
export type OpeningKind = "door" | "window";

/**
 * What fills a door's rough opening, and so what the plan sells for it.
 *
 * `"prehung"` is a unit bought to fit the hole: the plan frames the rough opening and
 * stops there. The other two are built on site, the way most sheds are — a skin cut
 * from a sheet of the wall's own siding, on a frame of 2x4 laid flat behind it with a
 * brace in each bay, hung in the opening against stops and swinging out.
 * `"siteBuiltDouble"` is two of those leaves meeting in the middle. The plan then
 * carries the door's own pieces, its cut list, a door detail, its hardware and the
 * steps to build and hang it. Spec version 6 and later.
 */
export type DoorStyle = "prehung" | "siteBuilt" | "siteBuiltDouble";

/**
 * Which way a door opens: the side, as the door is seen from outside, that the hinges
 * of the leaf you open are on. On a single door that is its hinge side; on a pair, the
 * leaf on that side is the one that opens and the other is bolted shut. A site-built
 * door's from spec version 8, and a prehung unit's - its hand, as it is ordered - from
 * spec version 16.
 */
export type DoorHand = "left" | "right";

/**
 * Whether a prehung door swings into the shed or out of it: with its hand, what the unit
 * is ordered by. A site-built door always swings out, against its stops, and has none.
 * Spec version 16 and later.
 */
export type DoorSwing = "in" | "out";

/**
 * The louvered vent in each gable end, by the rough opening it is bought to fit, width
 * by height in inches — or none. Both ends get the same one. Spec version 12 and later.
 */
export type GableVent = "none" | "12x12" | "12x18" | "14x24";

/** Every vent size a customer may choose, smallest first. */
export const GABLE_VENT_SIZES: readonly Exclude<GableVent, "none">[] = ["12x12", "12x18", "14x24"];

/** Roof slope as an exact ratio. The engine only accepts a run of 12. */
export interface Pitch {
  rise: number;
  run: number;
}

/**
 * A rough opening — the hole in the framing, not the door or window unit.
 *
 * `offset` is measured along the opening's own wall from its low-coordinate end:
 * from x = 0 on the front and back walls, from y = 0 on the left and right.
 */
export interface Opening {
  id: string;
  wall: WallName;
  type: OpeningKind;
  width: Sixteenths;
  height: Sixteenths;
  /** Bottom of the opening above the subfloor. Always zero for a door. */
  sill: Sixteenths;
  offset: Sixteenths;
  /**
   * Doors only, from spec version 6: absent means `"prehung"`. The engine echoes it on
   * every door of a v6 spec and on nothing older, because an older plan never named one.
   */
  doorStyle?: DoorStyle;
  /**
   * Site-built doors from spec version 8, and prehung doors too from spec version 16:
   * absent means a single door (or a prehung unit) hinges on the left and a pair opens on
   * the right. The engine echoes it on every door its version hands, drops it (with a
   * warning) from a window and from a prehung door of a spec older than 16, and refuses
   * it on a site-built door of a spec older than 8.
   */
  hand?: DoorHand;
  /**
   * Prehung doors only, from spec version 16: absent means `"in"`. The engine echoes it
   * on every prehung door of a v16 spec, drops it (with a warning) from a window or a
   * site-built door, and refuses it on an older spec.
   */
  swing?: DoorSwing;
  /**
   * Doors only, from spec version 15: a ramp from the deck down to the ground in front of
   * the door. Absent means none. The engine echoes it only where there is one, drops it
   * (with a warning) from a window and from any door of a building on a slab, whose floor
   * is at the ground already, and refuses it on an older spec.
   */
  ramp?: boolean;
}

/**
 * The framing stock the customer chose, as opposed to the stock the span table chose
 * for them.
 *
 * `wallStock` is a straight choice — a shed's stud wall is never span-limited, so 2x6
 * is about the insulation that fits between the studs and nothing else. The two
 * minimums are *floors* under the span table's answer: the engine raises its choice to
 * meet them and never lowers it, so asking for less than a span needs buys nothing.
 */
export interface FramingSpec {
  /** `"2x4"` or `"2x6"`. Anything else is refused. */
  wallStock: Stock;
  /** A floor under the span table's floor joist: `"2x6"` through `"2x10"`. */
  minJoistStock: Stock;
  /** A floor under the span table's rafter, on the same terms. */
  minRafterStock: Stock;
}

export interface RoofSpec {
  style: RoofStyle;
  support: RoofSupport;
  pitch: Pitch;
  eaveOverhang: Sixteenths;
  rakeOverhang: Sixteenths;
  rafterSpacing: Sixteenths;
  /**
   * Where a saltbox's ridge stands, in twelfths of the width from the low left wall:
   * `MIN_RIDGE_TWELFTHS` to `MAX_RIDGE_TWELFTHS`. Saltboxes only, from spec version 10:
   * absent means `DEFAULT_RIDGE_TWELFTHS` (two thirds), what every older saltbox was, and
   * the engine echoes it on every v10 saltbox and drops it, with a warning, from any
   * other roof.
   */
  ridgeTwelfths?: number;
  /**
   * The vent in each gable end — on a lean-to, each end wall. From spec version 12:
   * absent means `"none"`, what every older plan was, and the engine echoes it on every
   * v12 spec and refuses anything else on an older one. A size the gables have no room
   * for is refused; `PlanOk.gableVents.fits` says which sizes this building takes.
   */
  gableVent?: GableVent;
}

/** The ridge position a saltbox gets when its spec names none: two thirds across. */
export const DEFAULT_RIDGE_TWELFTHS = 8;
/** The range the engine accepts for a saltbox's ridge, in twelfths of the width. */
export const MIN_RIDGE_TWELFTHS = 7;
export const MAX_RIDGE_TWELFTHS = 10;

/**
 * The spec is the product: tiny, serializable user intent, and the only thing that
 * gets stored and handed to the paid-PDF generator.
 *
 * Every field is optional on the way in — the engine fills in its own defaults —
 * and every field comes back populated on the way out.
 */
export interface Spec {
  kind: Kind;
  version: number;
  footprint: { width: Sixteenths; length: Sixteenths };
  /** Subfloor top to the top of the double top plate. */
  wallHeight: Sixteenths;
  roof: RoofSpec;
  studSpacing: Sixteenths;
  joistSpacing: Sixteenths;
  framing: FramingSpec;
  foundation: Foundation;
  siding: Siding;
  openings: Opening[];
  /**
   * The longest board the plan buys just to pack pieces onto — what the customer can get
   * home — and one of the lengths a yard sells (8' to 20'). From spec version 8: absent
   * means twelve feet, which every older plan was cut for, and the engine echoes it only
   * on a v8 spec. It changes the cut list and nothing it is cut from.
   */
  haulLength?: Sixteenths;
  /**
   * The ground snow load the roof is sized for, in pounds per square foot: one of
   * `GROUND_SNOW_LOADS`. From spec version 11: absent means `DEFAULT_GROUND_SNOW_LOAD`,
   * which every older plan was sized for, and the engine echoes it only on a v11 spec. It
   * sizes the rafters, a ridge beam and its posts, and the headers over openings.
   */
  groundSnowLoad?: number;
}

/** The ground snow loads a plan may be sized for, in psf, lightest first. */
export const GROUND_SNOW_LOADS: readonly number[] = [30, 40, 50, 70];
/** The ground snow load a spec that names none is sized for — every plan before v11. */
export const DEFAULT_GROUND_SNOW_LOAD = 30;

/** What a caller may send. Anything omitted takes the engine's default. */
export type SpecInput = {
  [K in keyof Spec]?: K extends "roof"
    ? Partial<RoofSpec> & { pitch?: Partial<Pitch> }
    : K extends "footprint"
      ? Partial<Spec["footprint"]>
      : K extends "framing"
        ? Partial<FramingSpec>
        : Spec[K];
};

// ---- model -----------------------------------------------------------------

/** A point or a vector in engine space, exact to the sixteenth. */
export interface Vec3 {
  x: Sixteenths;
  y: Sixteenths;
  z: Sixteenths;
}

/**
 * An un-normalized integer direction. A unit vector for a sloped rafter would be
 * irrational; `(-4, 0, 12)` is the exact perpendicular of a 4:12 slope. Normalize
 * at the point of use, in whatever precision you render with.
 */
export interface Dir3 {
  x: number;
  y: number;
  z: number;
}

/** Which face of the building a piece belongs to. Filter on this for an elevation. */
export type Assembly =
  | "floor"
  | "wall:front"
  | "wall:back"
  | "wall:left"
  | "wall:right"
  | "roof";

/**
 * Whether a board is pressure treated: the second half of what to buy.
 *
 * A treated 2x6 and a plain one are the same `Stock` and not the same product — a
 * different rack, a different price, and different fasteners — so the bill lists them
 * as two lines and the cut list never packs one board with both. Absent means plain,
 * which is nearly every board in a shed.
 */
export type Treatment = "pressureTreated";

export type Role =
  | "skid"
  | "floorBeam"
  | "rimJoist"
  | "floorJoist"
  | "endJoistDoubler"
  | "sillPlate"
  | "bottomPlate"
  | "topPlate"
  | "capPlate"
  | "stud"
  | "cornerPost"
  | "kingStud"
  | "jackStud"
  | "header"
  | "roughSill"
  | "cripple"
  | "nailer"
  | "gableStud"
  | "ventStud"
  | "ventBlock"
  | "ventCripple"
  | "ridgeBoard"
  | "rafter"
  | "collarTie"
  | "fascia"
  | "friezeBlock"
  | "outrigger"
  | "rakeBoard"
  | "rafterTie"
  | "tieSplice"
  | "ridgePost"
  | "doorStile"
  | "doorRail"
  | "doorBrace"
  | "doorStop"
  | "astragal"
  | "rampLedger"
  | "rampStringer"
  | "rampDecking";

/**
 * The build-order phase a piece belongs to, in the order the work happens.
 *
 * `wallRaise` owns no members — standing and bracing the walls is work with no wood
 * in it — and is still a phase, or the sequence skips from a wall lying on the deck
 * to a plate over four standing ones.
 */
export type Stage =
  | "floor"
  | "walls"
  | "wallRaise"
  | "topPlates"
  | "rafters"
  | "sheathing"
  | "roofing"
  | "doors"
  /** The door ramps, built last, in front of doors already hung. */
  | "ramp";

/**
 * How one end of a member is cut. `square` is the saw at ninety degrees; `plumb`
 * stands vertical once the member is installed and `seat` lies level — the two cuts
 * a sloped member takes from the same pitch.
 */
export type CutKind = "square" | "plumb" | "seat";

/** One cut across a member, as the exact pitch that defines it. Zeroes for square. */
export interface Cut {
  kind: CutKind;
  rise: number;
  run: number;
}

/**
 * The notch that seats a rafter on a wall: a level `seat` cut bearing on the top
 * plate and a plumb `heel` cut down the wall's outer face.
 */
export interface Birdsmouth {
  /** Distance from the member's `start` to the heel cut, along the member. */
  fromStart: Sixteenths;
  seat: Sixteenths;
  heel: Sixteenths;
}

/**
 * A member ripped down from wider stock, and the bevel along the cut edge.
 *
 * Nearly all framing is used at the size it is bought. A frieze block is not: it
 * fills the gap between the top plate and the roof sheathing, which is never a stock
 * width, so it is ripped to fit with the saw tilted to the roof's pitch.
 *
 * `width` is the finished wide face on the **long** side of the bevel; the short side
 * is that less `thickness * rise / run`. The bevel rises toward the member's own
 * thickness direction (`axis x up`), so a block and its mirror on the far wall are one
 * description read from two headings.
 */
export interface Rip {
  width: Sixteenths;
  rise: number;
  run: number;
}

/**
 * A member's cut geometry, present only when the member has any. Absent means square
 * cut at both ends, no notch and no rip, which is most of a shed.
 */
export interface Cuts {
  start?: Cut;
  end?: Cut;
  birdsmouth?: Birdsmouth;
  /**
   * A second notch farther along the member: a lean-to rafter crossing its high wall,
   * heel down that wall's inside face. The same shape the same way round as the first.
   */
  upperBirdsmouth?: Birdsmouth;
  rip?: Rip;
}

/**
 * `doorSkin` is the face of a site-built door: a sheet of the wall's siding cut to the
 * leaf, bought as its own sheet and not one of the wall's.
 */
export type Sheet = "floorSheathing" | "roofSheathing" | "siding" | "doorSkin";

/**
 * Nominal trade names. A "2x4" is 1 1/2" by 3 1/2"; ask the engine, not the name.
 *
 * The two LVL stocks are laminated veneer lumber, named by their actual size: a header
 * over an opening no two-by header spans, from spec version 17, and never a framing
 * choice a spec can name.
 */
export type Stock =
  | "2x4"
  | "2x6"
  | "2x8"
  | "2x10"
  | "2x12"
  | "4x4"
  | "4x6"
  | "1-3/4x11-7/8 LVL"
  | "1-3/4x14 LVL";

/**
 * One cut piece of lumber.
 *
 * `start` and `end` are the ends of the piece's centerline, so a mesher sweeps the
 * cross-section along that segment. `up` points along the profile's wide face; the
 * thickness runs along `axis × up`.
 *
 * `length` is the board to order and saw: the extent along the member's own axis,
 * which for an angled end is longer than the centerline because the cut runs
 * diagonally across the stock. `cuts` says what those angles are.
 */
export interface Member {
  id: string;
  role: Role;
  assembly: Assembly;
  stage: Stage;
  stock: Stock;
  length: Sixteenths;
  start: Vec3;
  end: Vec3;
  up: Dir3;
  cuts?: Cuts;
  /** Absent means plain. Set on the wood that stands on the ground or on concrete. */
  treatment?: Treatment;
}

/**
 * One rectangular sheet. `origin` is a corner and `uEdge`/`vEdge` are the edge
 * vectors from it, so the rectangle works on the sloped roof without a rotation.
 */
export interface Panel {
  id: string;
  sheet: Sheet;
  assembly: Assembly;
  stage: Stage;
  origin: Vec3;
  uEdge: Vec3;
  vEdge: Vec3;
  uLength: Sixteenths;
  vLength: Sixteenths;
}

/**
 * What the building stands on where it is not wood.
 *
 * Concrete is neither a board nor a sheet, so it is neither a member nor a panel:
 * nothing about it is cut to a length and nothing about it is bought by the sheet.
 * `pierBlock` is one precast block; `slab` is the whole pour, and on a slab it is the
 * floor — there are no joists, no rim joists and no deck above it.
 */
export type Groundwork = "pierBlock" | "slab";

/**
 * One piece of groundwork, as the axis-aligned box it occupies.
 *
 * `center` is the middle of the box and `size` its full extent along each axis — the
 * only thing in the model stated as a solid rather than as a centerline, because a
 * slab has no axis to run along. A pier block is given at its base: the taper a cast
 * block has is not modeled, since what bears on the ground is the base.
 */
export interface Footing {
  id: string;
  groundwork: Groundwork;
  assembly: Assembly;
  stage: Stage;
  center: Vec3;
  size: Vec3;
}

/**
 * The extent of member centerlines, panel corners and footing boxes — not of the
 * solids of the lumber, whose half-thicknesses poke past it. Exact in integer math,
 * which is what makes it stable to assert against.
 */
export interface Bounds {
  min: Vec3;
  max: Vec3;
}

export interface Model {
  bounds: Bounds;
  members: Member[];
  panels: Panel[];
  /** Empty on skids, which stand on the ground and buy nothing to stand on. */
  footings: Footing[];
}

// ---- bill of materials -----------------------------------------------------

/**
 * The bill is the **net takeoff** — what the building contains. What you actually
 * buy is the cut list's `orders` (lumber) plus these `sheets`, and the gap between
 * the two is the waste the cut list exists to make small.
 */

/**
 * Board feet, in thousandths of a board foot. `5333` is 5 1/3.
 *
 * Board feet are quoted on nominal size, the way lumber is priced: eight feet of
 * 2x4 is 5.333 bf, not the 3.5 its dressed 1 1/2" by 3 1/2" section would suggest.
 * Thousandths keep it an integer, because a float on this seam would be a float in
 * the BOM.
 */
export type BoardFeetThousandths = number;

export interface LumberLine {
  stock: Stock;
  /** Absent means plain. A treated line is a separate line, because it is a separate
   * product at the yard. */
  treatment?: Treatment;
  /** Members cut from this stock. */
  pieces: number;
  /** Summed cut lengths — the wood in the building, not the wood on the receipt. */
  totalLength: Sixteenths;
  boardFeetThousandths: BoardFeetThousandths;
}

export interface SheetLine {
  sheet: Sheet;
  /** Rectangles the model places. Many are offcuts of a full sheet. */
  pieces: number;
  /** Full 4x8 sheets to buy. */
  sheets: number;
  areaSquareInches: number;
}

export interface Bom {
  /** Lightest stock first, and only for stock the model actually uses. */
  lumber: LumberLine[];
  sheets: SheetLine[];
  totals: {
    totalLength: Sixteenths;
    boardFeetThousandths: BoardFeetThousandths;
    sheets: number;
  };
}

// ---- cut list --------------------------------------------------------------

/**
 * One required piece, traced back to the member it becomes.
 *
 * The cuts come with it because a length alone is an instruction to make the wrong
 * board: a rafter's is the same number whether it is cut square or plumb, and the
 * person at the saw finds out which here or not at all. Absent means square.
 */
export interface CutPiece {
  memberId: string;
  role: Role;
  length: Sixteenths;
  startCut?: Cut;
  endCut?: Cut;
  birdsmouth?: Birdsmouth;
  /** A lean-to rafter's notch over its high wall. */
  upperBirdsmouth?: Birdsmouth;
  /** Rip the stock down to this before cutting to length. */
  rip?: Rip;
  /**
   * Where the piece starts on its board, measured from the board's end: the pieces
   * before it, and one kerf after each.
   */
  at: Sixteenths;
  /**
   * The piece's wide face as it is marked out on the board, `[along, across]` from its
   * start and its underside: along runs 0 to `length` exactly, across 0 to the width it
   * is used at. Corners are exact; a birdsmouth's seat rounds once to the sixteenth.
   * Absent means the plain rectangle — square both ends, the board's full `width`.
   */
  outline?: FacePoint[];
}

/** `[along, across]` on a board's wide face. */
export type FacePoint = [Sixteenths, Sixteenths];

/** One purchased board and everything cut from it, in cutting order. */
export interface CutBoard {
  stock: Stock;
  /** Which rack it came off. Every piece on the board shares it. */
  treatment?: Treatment;
  /** The length you buy — always one the yard stocks. */
  stockLength: Sixteenths;
  /** The board's wide face, which every piece's outline is drawn across. */
  width: Sixteenths;
  used: Sixteenths;
  /** Sawdust: one kerf between each pair of pieces. */
  kerfTotal: Sixteenths;
  /** The drop: `stockLength - used - kerfTotal`. */
  waste: Sixteenths;
  pieces: CutPiece[];
}

/** A shopping line: how many boards of one stock and treatment at one length. */
export interface BoardOrder {
  stock: Stock;
  treatment?: Treatment;
  length: Sixteenths;
  count: number;
}

export interface CutPlan {
  /** What the blade turns to sawdust at every cut. */
  kerf: Sixteenths;
  /**
   * The longest board the plan will buy purely to pack pieces onto.
   *
   * A twelve-footer rides home in a pickup; a twenty-footer needs a trailer, so the
   * default spends a few points of waste on the trip home. A board longer than this
   * appears only when one piece needs it — a rim joist, a ridge board.
   */
  maxHaulLength: Sixteenths;
  /** Grouped lightest stock first. */
  boards: CutBoard[];
  /** The same boards rolled up into what you hand the yard. */
  orders: BoardOrder[];
  /**
   * Pieces longer than any board the yard stocks — a rim joist on a wide shed —
   * which have to be spliced on site. The envelope carries a `member_exceeds_stock`
   * warning for each one.
   */
  oversize: CutPiece[];
  totals: {
    boards: number;
    purchasedLength: Sixteenths;
    boardFeetThousandths: BoardFeetThousandths;
    wasteLength: Sixteenths;
  };
}

// ---- drawings --------------------------------------------------------------

/**
 * Which way a drawing looks at the building, and what it looks through.
 *
 * Each view cuts. An elevation draws only what is in front of a plane just inside the
 * wall it faces, so the back wall's studs are not stacked behind the front wall's.
 * `top` is the roof frame and the plates it lands on; `floor` is the floor frame with
 * nothing standing on it. Both plans look down with north up the page, so the same
 * corner is in the same place on either sheet.
 *
 * The `panel*` views cut a third way, by assembly: one wall's own framing and nothing
 * else. They exist because an elevation cannot be that drawing — everything in front
 * of its plane is genuinely in front of it, so an eave elevation carries the rafter
 * tails over the wall and the gable walls' framing where it crosses, which is right
 * for a picture of the building and wrong for a drawing you frame a wall from. A panel
 * is drawn from the same side as its elevation, so one wall reads the same way round
 * on both sheets.
 *
 * `section` cuts a fourth way, and it is the only view where the plane is the subject
 * rather than the filter: a saw through the building at mid-length, with the faces it
 * left drawn in a `cut` group so they can be inked heavier than the frame standing
 * beyond them. It is the drawing that answers how the floor, the walls and the roof
 * meet, which every other sheet shows one side of.
 *
 * The `sheet*` views are the one place a drawing shows sheet goods, and they are the
 * exception the "framing drawings, not sheet goods" rule names rather than a breach of
 * it: the sheet drawn *as a sheet*, unfilled, over the wall's own framing, so a joint
 * can be seen landing on a stud. `openingDetail` appears once per distinct opening
 * rather than once per building, so a plan set has as many of them as the design has
 * differently framed holes in it — which is why a drawing carries a `label`.
 * `doorDetail` is the same again for each distinct site-built door: its leaves' frames
 * and braces and the stops round it, seen from inside. `rampDetail` is last: a door ramp
 * from the side, once, because every ramp on a building is the same in profile.
 */
export type View =
  | "front"
  | "back"
  | "left"
  | "right"
  | "top"
  | "floor"
  | "section"
  | "panelFront"
  | "panelBack"
  | "panelLeft"
  | "panelRight"
  | "sheetFloor"
  | "sheetFront"
  | "sheetBack"
  | "sheetLeft"
  | "sheetRight"
  | "sheetRoof"
  /** A saltbox's short slope. Only a saltbox's plan set has one. */
  | "sheetRoofShort"
  | "openingDetail"
  | "doorDetail"
  /** A door ramp seen from the side. Once per plan, after the doors, when any door has one. */
  | "rampDetail";

/**
 * One dimensioned orthographic drawing, as an SVG document.
 *
 * These are *framing* drawings: they draw the members and not the sheet goods,
 * because the engine bills siding in whole sheets and the diagonal cut along a gable
 * rake is made on site — a drawing that painted the panels as placed would stand a
 * rectangle of siding above the roof line and call it the building's outline.
 *
 * **The SVG is a deliberately small dialect**, and `@planforge/pdf` depends on that:
 * three element types (`g`, `path`, `text`), one element per line, style on the group
 * and geometry on the element. The engine has a test that fails if it ever emits a
 * fourth. Read it with the reader in `@planforge/pdf`, not with a DOM parser — there
 * is no DOM in the worker that will one day regenerate a purchased plan.
 *
 * Coordinates are **mils** — thousandths of an inch of paper, integers — with y
 * downward, in a `viewBox` of `widthMils` by `heightMils`. `scale` is the denominator
 * of the architect's scale the engine picked from a fixed ladder, so `48` is
 * 1/4" = 1'-0" and is a fact about the drawing rather than a guess for the reader.
 */
export interface Drawing {
  view: View;
  scale: number;
  widthMils: number;
  heightMils: number;
  /**
   * Which sheet of this view it is, where a building has more than one.
   *
   * Empty on every view there is exactly one of; the opening's own mark on a
   * `openingDetail`. The composer titles the page with the view and this together.
   */
  label: string;
  svg: string;
}

// ---- consumables -----------------------------------------------------------

/**
 * What it stands on; nails and hardware; what keeps water out; what goes on last.
 *
 * `groundwork` is concrete and precast blocks. They are here rather than in the bill
 * because the bill is a lumber and sheet takeoff whose totals are board feet and sheet
 * counts — concrete has neither — and because this is the page that prints the rate
 * beside every figure, which is exactly what a volume of concrete needs.
 */
export type ConsumableKind =
  | "groundwork"
  | "fastener"
  | "weatherproofing"
  | "finish"
  | "hardware";

/**
 * Where a line's number came from, which is the whole reason the field exists.
 *
 * `derived` was worked out from the building at a rate the line states — roof area
 * into shingles, a bearing rafter into a hurricane tie. `allowance` is a starting
 * figure for something the spec does not decide: paint depends on the tin, trim on a
 * profile nobody has chosen. Both carry a quantity, because "some paint" helps
 * nobody; the field is what stops the second kind being read as the first.
 */
export type ConsumableBasis = "derived" | "allowance";

export interface ConsumableLine {
  item: string;
  kind: ConsumableKind;
  basis: ConsumableBasis;
  /** Whole purchasable units, always rounded up. You cannot buy most of a roll. */
  quantity: number;
  /**
   * `lb`, `each`, `tube`, `roll`, `square`, `bundle`, `gal`, `piece`, `ft`, `sheet`,
   * `section`, `cu ft`.
   */
  unit: string;
  /** The rate or count behind the quantity, in words. */
  derivation: string;
}

export interface Consumables {
  /**
   * Groundwork, then fasteners, then weatherproofing, then finishes — the order the
   * work happens in.
   */
  lines: ConsumableLine[];
}

// ---- the build guide -------------------------------------------------------

/**
 * One step of the work.
 *
 * `stage` is the build-order phase it belongs to, and the steps arrive in that order —
 * the guide walks the stage every member has carried since the framing pass rather
 * than deciding a sequence of its own.
 *
 * The split between `body` and `figures` is the free preview's line drawn where it is
 * drawn everywhere else: `body` is the instruction, which is what proves the guide is
 * real, and `figures` are the quantities, which are what is being sold.
 */
export interface GuideStep {
  stage: Stage;
  title: string;
  body: string;
  /** Per-design counts and lengths, one short line each. Every one is a quantity. */
  figures: string[];
}

/**
 * The order of the work, for this shed and no other.
 *
 * A projector like the bill of materials: it reads the model and adds nothing to it.
 * What it contributes is sequence — that the walls are framed flat on the deck and
 * stood up afterwards is a fact no table in the envelope carries, and the one a first
 * timer most needs.
 */
export interface BuildGuide {
  steps: GuideStep[];
}

/**
 * Where the design stands against the permit-exempt caps. `withinStrict` is under
 * every cap, the stricter 120 sq ft floor area included; `withinCommon` is under every
 * cap but past 120 sq ft; `exceeded` is past at least one, and each one passed is also
 * a `permit_exempt_*` warning — the engine computes both from one check.
 */
export type PermitVerdict = "withinStrict" | "withinCommon" | "exceeded";

/**
 * The design measured against the permit-exempt caps, with the caps beside the
 * measurements so a client can say "96 of 200 sq ft" without restating either number.
 *
 * Heights run from the bottom of whatever the building stands on to the top of the
 * ridge, which is what a height cap measures — so the same shed reads differently on
 * skids, piers and a slab.
 */
export interface Permit {
  verdict: PermitVerdict;
  floorAreaSquareInches: number;
  /** The model residential code's exemption, 200 sq ft. Past it is a warning. */
  maxFloorAreaSquareInches: number;
  /** The stricter 120 sq ft some jurisdictions use. Never a warning on its own. */
  strictFloorAreaSquareInches: number;
  width: Sixteenths;
  maxWidth: Sixteenths;
  length: Sixteenths;
  maxLength: Sixteenths;
  wallHeight: Sixteenths;
  maxWallHeight: Sixteenths;
  overallHeight: Sixteenths;
  maxOverallHeight: Sixteenths;
}

/**
 * A gable vent's rough opening as the engine framed it, in an opening's own terms:
 * `offset` along its wall from x = 0, `sill` above the subfloor.
 */
export interface VentOpening {
  wall: "front" | "back";
  width: Sixteenths;
  height: Sixteenths;
  sill: Sixteenths;
  offset: Sixteenths;
}

/**
 * The gable vents: which sizes this building has room for — the engine's own rule, so a
 * configurator offers exactly the sizes it would accept — and where the chosen one went,
 * front then back. Empty `vents` when the spec has none.
 */
export interface GableVents {
  fits: Exclude<GableVent, "none">[];
  vents: VentOpening[];
}

// ---- the response envelope -------------------------------------------------

/**
 * `code` is the stable half — branch on it. `message` is prose and may be reworded.
 * `field` is a dotted path into the spec, or empty.
 */
export interface Diagnostic {
  code: string;
  message: string;
  field: string;
}

interface PlanBase {
  engineVersion: string;
  /** The version a new spec is written at — `SPEC_VERSION`. */
  specVersion: number;
  /** The oldest version this engine still builds. A stored spec below it is a plan
   * that has to be regenerated from its frozen envelope rather than from the spec. */
  oldestSpecVersion: number;
  errors: Diagnostic[];
  warnings: Diagnostic[];
}

export interface PlanOk extends PlanBase {
  ok: true;
  /**
   * The normalized spec the model was actually built from — clamps and all. Store
   * *this*, not what you sent, so regenerating later is byte-identical.
   */
  spec: Spec;
  model: Model;
  bom: Bom;
  cutList: CutPlan;
  /**
   * The plan set, in printing order: the four elevations, the two plans, the section,
   * the four wall panels, the six sheet layouts, and then one rough-opening detail per
   * distinct opening.
   *
   * The layouts run in the order the sheets go on — the deck, the four walls, one
   * slope of the roof — and a building with no sheets on a surface simply has no
   * layout for it: a slab has no floor deck, so `sheetFloor` is not in its set. Read
   * the views off the drawings rather than assuming a fixed length.
   *
   * They ride in the envelope rather than behind a call of their own because a bought
   * plan is regenerated from its *stored envelope*, not from a rerun of whatever
   * engine is deployed the day the customer comes back. An envelope missing its
   * drawings would not be the thing they bought.
   */
  drawings: Drawing[];
  /** Everything the build needs that is not a board or a sheet. */
  consumables: Consumables;
  /** The order of the work, for this shed and no other. */
  guide: BuildGuide;
  /**
   * The design against the permit-exempt caps, for the configurator's panel.
   *
   * Since engine 0.19. An envelope frozen into a purchase before that has none, which
   * is why nothing that recomposes a frozen purchase reads it: the panel reads a live
   * plan, and the PDF prints the warnings, which every envelope carries.
   */
  permit: Permit;
  /**
   * The gable vents, from spec version 12. Absent on an older plan — which is why the
   * composer and the 3D view read it as "no vents" rather than requiring it: a purchase
   * is recomposed from the envelope it was sold as.
   */
  gableVents?: GableVents;
}

export interface PlanFailed extends PlanBase {
  ok: false;
}

export type Plan = PlanOk | PlanFailed;
